import React, { useState, useEffect, useRef } from 'react';

import { uploadImageToCloudinaryWithProgress } from '../../../../api/external/cloudinary/upload';

import { getAllAuthors } from '../../../../api/services/blogPostAuthorsApi';
import { getAllCategories } from '../../../../api/services/blogPostCategoriesApi';

import EditModalShell from '../forms/EditModalShell/EditModalShell';
import LoadingModal from '../LoadingModal/LoadingModal';
import AdminProgressBar from '../../AdminProgressBar/AdminProgressBar';
import ImageInputWithPreview from '../forms/ImageInputWithPreview/ImageInputWithPreview';
import FormField from '../forms/FormField/FormField';

import './BlogPostModal.css';

const BlogPostModal = ({ onClose, onSubmit, blogPost, mode }) => {
  const initialFormState = {
    // fields for Blog Page
    title: '',
    author: '',
    content: '',
    excerpt: '',
    imageUrl: '', // image path
    imagePublicId: '',
    publishedAt: '',
    categories: [],
    featured: false,
    showOnBlog: true,
    disableCommentsOnBlog: false,

    // fields for Home Page
    homeTitle: '',
    homeExcerpt: '',
    homeImageUrl: '', // image path
    homeImagePublicId: '',
    showOnHome: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [selectedFiles, setSelectedFiles] = useState({}); // raw files
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});

  const [excerptMode, setExcerptMode] = useState('manual');
  const [editingExcerpt, setEditingExcerpt] = useState('');

  const [authorOptions, setAuthorOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const {
    title,
    author,
    content,
    excerpt,
    imageUrl,
    publishedAt,
    categories,
    featured,
    showOnBlog,
    disableCommentsOnBlog,
    homeTitle,
    homeExcerpt,
    homeImageUrl,
    showOnHome,
  } = formData;

  const firstInputRef = useRef();

  /* ---------- Load data ---------- */

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const authors = await getAllAuthors();
        setAuthorOptions(
          authors.map((a) => ({ value: a.name, label: a.name })),
        );
      } catch (err) {
        console.error('Failed to fetch authors:', err);
      }
    };

    fetchAuthors();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await getAllCategories();
        setCategoryOptions(categories.map((c) => c.name));
      } catch (err) {
        console.error('Failed to fetch categories', err);
      }
    };
    fetchCategories();
  }, []);

  /* ---------- Init form ---------- */

  useEffect(() => {
    const today = getLocalDateInputValue();

    setFormData({
      title: blogPost?.title || '',
      author: blogPost?.author?.name || '',
      content: blogPost?.content || '',
      excerpt: blogPost?.excerpt || '',
      imageUrl: blogPost?.imageUrl || '',
      imagePublicId: blogPost?.imagePublicId || '',
      publishedAt:
        mode === 'edit' ? blogPost?.publishedAt?.split('T')[0] || '' : today,
      categories: blogPost?.categories?.map((c) => c?.name || '') || [],
      featured: blogPost?.featured || false,
      showOnBlog: blogPost?.showOnBlog ?? true,
      disableCommentsOnBlog: blogPost?.disableCommentsOnBlog || false,

      homeTitle: blogPost?.homeTitle || '',
      homeExcerpt: blogPost?.homeExcerpt || '',
      homeImageUrl: blogPost?.homeImageUrl || '',
      homeImagePublicId: blogPost?.homeImagePublicId || '',
      showOnHome: blogPost?.showOnHome || false,
    });

    setImagePreviewUrls({
      imageUrl: blogPost?.imageUrl || '',
      homeImageUrl: blogPost?.homeImageUrl || '',
    });

    setSelectedFiles({});
    setEditingExcerpt(blogPost?.excerpt || '');

    firstInputRef.current?.focus();
  }, [mode, blogPost]);

  useEffect(() => {
    if (excerptMode === 'auto' && content) {
      const autoExcerpt = content.split(' ').slice(0, 30).join(' ') + '...';
      setFormData((prev) => ({ ...prev, excerpt: autoExcerpt }));
    }
  }, [excerptMode, content]);

  /* ---------- Helpers ---------- */

  function getLocalDateInputValue(date = new Date()) {
    const offset = date.getTimezoneOffset();

    const localDate = new Date(date.getTime() - offset * 60 * 1000);

    return localDate.toISOString().split('T')[0];
  }

  /* ---------- Handlers ---------- */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCategoriesChange = (newCategories) =>
    setFormData((prev) => ({
      ...prev,
      categories: newCategories,
    }));

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    setSelectedFiles((prev) => ({ ...prev, [name]: file }));

    setImagePreviewUrls((prev) => ({
      ...prev,
      [name]: URL.createObjectURL(file),
    }));
  };

  const handleRemoveImage = (name) => {
    setSelectedFiles((prev) => ({ ...prev, [name]: null }));
    setImagePreviewUrls((prev) => ({ ...prev, [name]: null }));

    setFormData((prev) => ({
      ...prev,
      [name]: '',
      [`${name.replace(/Url$/, '')}PublicId`]: '',
    }));
  };

  /* ---------- Submit ---------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setProgress(0);

    try {
      let finalData = { ...formData };

      if (finalData.publishedAt) {
        const [year, month, day] = finalData.publishedAt.split('-').map(Number);

        const localDate = new Date(year, month - 1, day);

        // keep midnight (represents "publish date")
        finalData.publishedAt = localDate.toISOString();
      }

      if (selectedFiles?.imageUrl) {
        const uploaded = await uploadImageToCloudinaryWithProgress(
          selectedFiles.imageUrl,
          setProgress,
          'blog/posts/blog',
          [0, 50],
        );

        finalData.imageUrl = uploaded.url;
        finalData.imagePublicId = uploaded.publicId;
      }

      if (selectedFiles?.homeImageUrl) {
        const uploaded = await uploadImageToCloudinaryWithProgress(
          selectedFiles.homeImageUrl,
          setProgress,
          'blog/posts/home',
          [50, 100],
        );

        finalData.homeImageUrl = uploaded.url;
        finalData.homeImagePublicId = uploaded.publicId;
      }

      setProgress(100);

      await onSubmit(finalData);
    } catch (err) {
      console.error(err);
      alert('Upload failed.');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  /* ---------- Render ---------- */

  return (
    <EditModalShell
      formId='blog-post-modal-form'
      onClose={onClose}
      onSubmit={handleSubmit}
      mode={mode}
      isLoading={loading}
      itemLabel='Blog Post'
    >
      {loading && (
        <LoadingModal>
          <AdminProgressBar progress={progress} />
        </LoadingModal>
      )}

      <div className='blog-post-modal-content-container'>
        <div className='blog-post-modal-date-field-container'>
          <FormField
            type='date'
            name='publishedAt'
            label='Date'
            value={publishedAt}
            onChange={handleDateChange}
            required
          />
        </div>
        <div className='blog-post-modal-pages-container'>
          <div className='blog-post-modal-blog-page-container'>
            <h4 className='blog-post-model-pages-title'>Blog Page</h4>

            <ImageInputWithPreview
              name='imageUrl'
              imagePreviewUrl={imagePreviewUrls?.imageUrl}
              imageUrl={imageUrl}
              selectedImgAlt='Selected Blog Post Image Preview'
              currentImgAlt='Current Blog Post'
              onFileChange={handleFileChange}
              onRemoveImage={handleRemoveImage}
            />
            <FormField
              type='text'
              name='title'
              label='Title'
              value={title}
              onChange={handleInputChange}
              ref={firstInputRef}
              required
            />
            <FormField
              type='select'
              name='author'
              label='Author'
              value={author}
              onChange={handleInputChange}
              options={authorOptions}
              noSelectOptionMsg={'Select an author'}
              required
            />
            <FormField
              type='textarea'
              name='content'
              label='Content'
              value={content}
              onChange={handleInputChange}
              required
            />
            <div className='excerpt-mode-selector'>
              <label>
                <input
                  type='radio'
                  name='excerptMode'
                  value='auto'
                  checked={excerptMode === 'auto'}
                  onChange={() => {
                    setEditingExcerpt(formData.excerpt);
                    setExcerptMode('auto');
                  }}
                />
                Auto-generate
              </label>
              <label>
                <input
                  type='radio'
                  name='excerptMode'
                  value='manual'
                  checked={excerptMode === 'manual'}
                  onChange={() => {
                    setExcerptMode('manual');
                    setFormData((prev) => ({
                      ...prev,
                      excerpt: editingExcerpt,
                    }));
                  }}
                />
                Manual
              </label>
            </div>
            <FormField
              type='textarea'
              name='excerpt'
              label='Excerpt'
              value={excerpt}
              onChange={handleInputChange}
              readOnly={excerptMode === 'auto'}
              required
            />
            <FormField
              type='categories'
              name='categories'
              label='Categories'
              value={categories}
              onChange={handleCategoriesChange}
              suggestions={categoryOptions}
            />
            <FormField
              type='checkbox'
              name='featured'
              label='Featured'
              value={featured}
              onChange={handleCheckboxChange}
            />
            <FormField
              type='checkbox'
              name='showOnBlog'
              label='Show on Blog Page'
              value={showOnBlog}
              onChange={handleCheckboxChange}
            />
            <FormField
              type='checkbox'
              name='disableCommentsOnBlog'
              label='Disable comments on Blog Page'
              value={disableCommentsOnBlog}
              onChange={handleCheckboxChange}
            />
          </div>
          <div className='blog-post-modal-home-page-container'>
            <h4 className='blog-post-model-pages-title'>Home Page</h4>
            <ImageInputWithPreview
              name='homeImageUrl'
              imagePreviewUrl={imagePreviewUrls?.homeImageUrl}
              imageUrl={homeImageUrl}
              selectedImgAlt='Selected Home Image Preview'
              currentImgAlt='Current Blog Post'
              noImgMsg='Upload a photo if different from Blog Page'
              onFileChange={handleFileChange}
              onRemoveImage={handleRemoveImage}
            />
            <FormField
              type='text'
              name='homeTitle'
              label={'Title'}
              value={homeTitle}
              onChange={handleInputChange}
            />
            <FormField
              type='textarea'
              name='homeExcerpt'
              label='Excerpt'
              value={homeExcerpt}
              onChange={handleInputChange}
            />
            <FormField
              type='checkbox'
              name='showOnHome'
              label='Show on Home Page'
              value={showOnHome}
              onChange={handleCheckboxChange}
            />
          </div>
        </div>
      </div>
    </EditModalShell>
  );
};

export default BlogPostModal;
